import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Descriptions, Tabs, Row, Col, Collapse, Skeleton, message } from 'antd';
import { FileTextOutlined, FormOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import SharedProductImage from '../../components/productImage';
import { isHandledByInterceptor } from '../../utils/apiError';
import StatusBadge from './StatusBadge';
import './style.less';

const routes = [
  { breadcrumbName: 'Барааны удирдлага' },
  { breadcrumbName: 'Барааны өөрчлөлтийн хүсэлт' },
];

/** Descriptions-ийн багана — жижиг дэлгэц дээр нэг багана болж эвхэгдэнэ */
const RESPONSIVE_COLUMN = { xs: 1, sm: 1, md: 2 };

/* --- helpers --- */

const numberFormat = new Intl.NumberFormat('mn-MN');

const formatNumber = (v) => (typeof v === 'number' ? numberFormat.format(v) : (v ?? '-'));

const formatPrice = (v) => (typeof v === 'number' ? `${numberFormat.format(v)}₮` : '-');

const formatDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');

const formatBool = (v) => (v ? 'Тийм' : 'Үгүй');

/** Тухайн талбарын аль нэг түлхүүр өөрчлөгдсөн эсэх */
const isFieldChanged = (changedFields, keys = []) =>
  !!changedFields && keys.some((k) => changedFields.has(k));

/* --- small UI bits --- */

const SectionHeader = ({ icon, children }) => (
  <div className="pcd-section-header">
    <span className="pcd-section-header__icon">{icon}</span>
    <h3 className="pcd-section-header__title">{children}</h3>
  </div>
);

const Section = ({ title, children, defaultOpen = true }) => (
  <Collapse
    expandIconPosition="end"
    defaultActiveKey={defaultOpen ? ['panel'] : []}
    items={[
      {
        key: 'panel',
        label: <span className="pcd-panel-title">{title}</span>,
        children,
      },
    ]}
  />
);

const FieldValue = ({ changed, children }) => (
  <span className={changed ? 'pcd-value pcd-value--changed' : 'pcd-value'}>{children}</span>
);

const ProductImage = ({ src }) => (
  <div className="pcd-image-box">
    <SharedProductImage
      src={src}
      size="100%"
      iconSize={64}
      preview={{ mask: 'Томруулах' }}
      style={{ borderRadius: 0 }}
    />
  </div>
);

/* --- талбаруудын тохиргоо --- */

const PRODUCT_FIELDS = [
  { label: 'Барааны нэр', keys: ['title'], value: (d) => d?.title || '-' },
  { label: 'Барааны тайлбар', keys: ['description'], value: (d) => d?.description || '-' },
  { label: 'Бренд', keys: ['brand'], value: (d) => d?.brand || '-' },
  {
    label: 'Буцаагддаг бараа эсэх',
    keys: ['isReturnable'],
    value: (d) => formatBool(d?.isReturnable),
  },
  { label: 'Категори', keys: ['category'], value: (d) => d?.category || '-' },
  {
    label: 'Дуусах хугацаатай эсэх',
    keys: ['willExpire'],
    value: (d) => formatBool(d?.willExpire),
  },
    {
    label: 'Эвдрэх аюултай эсэх',
    keys: ['isFragile'],
    value: (d) => formatBool(d?.isFragile),
  },
];

const OTHER_FIELDS = [
  {
    label: 'Өнгө, хэмжээ төрөл бүртгэх эсэх?',
    keys: ['attributes'],
    value: (d) => formatBool(d?.attributes),
  },
  { label: 'Өнгө', keys: ['attributes.color', 'color'], value: (d) => d?.attributes?.color || '-' },
  { label: 'Хэмжээ', keys: ['attributes.size', 'size'], value: (d) => d?.attributes?.size || '-' },
  { label: 'Төрөл', keys: ['attributes.type'], value: (d) => d?.attributes?.type || '-' },
  { label: 'Зарах үнэ', keys: ['price'], value: (d) => formatPrice(d?.price) },
  { label: 'Зайлшгүй байх нөөц', keys: ['minStock'], value: (d) => formatNumber(d?.minStock) },
  { label: 'Эзлэхүүн', keys: ['volume', 'vVolume'], value: (d) => formatNumber(d?.volume) },
];

const InfoDescriptions = ({ data, fields, changedFields }) => (
  <Descriptions layout="vertical" column={RESPONSIVE_COLUMN} size="middle" colon={false}>
    {fields.map(({ label, keys, value }) => (
      <Descriptions.Item key={label} label={label}>
        <FieldValue changed={isFieldChanged(changedFields, keys)}>{value(data)}</FieldValue>
      </Descriptions.Item>
    ))}
  </Descriptions>
);

/* --- овор хэмжээ --- */

const DIMENSION_FIELDS = [
  { label: 'Урт', keys: ['size.length', 'size', 'length'], value: (d) => formatNumber(d.length) },
  { label: 'Өргөн', keys: ['size.width', 'size', 'width'], value: (d) => formatNumber(d.width) },
  { label: 'Өндөр', keys: ['size.height', 'size', 'height'], value: (d) => formatNumber(d.height) },
  { label: 'Хэмжих нэгж', keys: ['sizeMeasure', 'size.measure'], value: (d) => d.sizeMeasure },
  { label: 'Жин', keys: ['weight', 'size.weight'], value: (d) => formatNumber(d.weight) },
  { label: 'Жингийн нэгж', keys: ['weightMeasure'], value: (d) => d.weightMeasure },
];

/**
 * `size` объектоос овор хэмжээг гаргана. Хэмжих нэгж нь өөрчлөлтийн хүсэлт дээр
 * ирээгүй үед хуучин мэдээллээс (fallback) авна.
 */
const readDimensions = (data, fallback) => {
  const size = data?.size && typeof data.size === 'object' ? data.size : {};

  return {
    length: size.length,
    width: size.width,
    height: size.height,
    weight: data?.weight ?? size.weight,
    sizeMeasure: size.measure || data?.sizeMeasure || fallback?.sizeMeasure || 'см',
    weightMeasure: data?.weightMeasure || fallback?.weightMeasure || 'кг',
  };
};

const DimensionsTable = ({ dimensions, changedFields }) => (
  <div className="pcd-dimensions">
    {DIMENSION_FIELDS.map(({ label, keys, value }) => (
      <div className="pcd-dimensions__cell" key={label}>
        <span className="pcd-dimensions__label">{label}</span>
        <FieldValue changed={isFieldChanged(changedFields, keys)}>
          {value(dimensions) ?? '-'}
        </FieldValue>
      </div>
    ))}
  </div>
);

/* --- нэг таб-ын агуулга --- */

const ProductPanel = ({ data, fallback, changedFields }) => (
  <Row gutter={[16, 16]} align="top">
    <Col xs={24} sm={8} md={6}>
      <ProductImage src={data?.imageCover} />
    </Col>

    <Col xs={24} sm={16} md={18}>
      <div className="pcd-sections">
        <Section title="Барааны мэдээлэл">
          <InfoDescriptions data={data} fields={PRODUCT_FIELDS} changedFields={changedFields} />
        </Section>

        <Section title="Овор хэмжээ">
          <DimensionsTable
            dimensions={readDimensions(data, fallback)}
            changedFields={changedFields}
          />
        </Section>

        <Section title="Бусад мэдээлэл">
          <InfoDescriptions data={data} fields={OTHER_FIELDS} changedFields={changedFields} />
        </Section>
      </div>
    </Col>
  </Row>
);

/* --- ерөнхий мэдээлэл --- */

const GENERAL_FIELDS = [
  { label: 'Огноо', render: (d) => formatDate(d?.createdAt) },
  { label: 'Хариулсан хэрэглэгч', render: (d) => d?.reviewedBy || '-' },
  { label: 'Үүсгэсэн хэрэглэгч', render: (d) => d?.merchant || '-' },
  { label: 'Төлөв', render: (d) => <StatusBadge status={d?.status} /> },
  { label: 'SKU', render: (d) => d?.productSku || '-' },
];

/* --- main page --- */

const ProductChangesDetail = () => {
  const { changeId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Өөрчлөгдсөн талбарууд — хоёр таб дээр дахин тооцоолохгүйн тулд memo хийв
  const changedFieldSet = useMemo(
    () => new Set((data?.changedFields || []).map((c) => c.field)),
    [data?.changedFields]
  );

  useEffect(() => {
    if (!changeId) return undefined;

    let mounted = true;
    setLoading(true);

    axios
      .get('/api/v1.0/product/updateRequestDetail', { params: { id: changeId } })
      .then((res) => {
        if (!mounted) return;
        if (res.data?.statusCode === 200) setData(res.data.result);
        // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
        else if (!isHandledByInterceptor(res.data)) message.error('Өгөгдөл татахад алдаа гарлаа.');
      })
      .catch((error) => {
        // Алдааны мэдэгдлийг axios interceptor аль хэдийн гаргасан — давхардуулахгүй
        console.error('updateRequestDetail: ', error);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [changeId]);

  return (
    <div className="product-change-detail">
      <PPPageHeader
        title={data?.updatedData?.title || data?.originalData?.title}
        breadcrumb={{ routes }}
        onBack={() =>
          history.push({
            pathname: '/product-changes',
            search: location.state?.search,
          })
        }
      />

      <div className="pp_table_wrapper">
        <SectionHeader icon={<FileTextOutlined />}>Ерөнхий мэдээлэл</SectionHeader>

        <Skeleton active loading={loading} paragraph={{ rows: 3 }}>
          <Descriptions column={RESPONSIVE_COLUMN} size="middle" labelStyle={{ width: 160 }}>
            {GENERAL_FIELDS.map(({ label, render }) => (
              <Descriptions.Item key={label} label={label}>
                <span className="pcd-value">{render(data)}</span>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Skeleton>
      </div>

      <div className="pp_table_wrapper">
        <SectionHeader icon={<FormOutlined />}>Барааны өөрчлөлтийн мэдээлэл</SectionHeader>

        <Skeleton active loading={loading} paragraph={{ rows: 6 }}>
          <Tabs
            defaultActiveKey="updated"
            destroyInactiveTabPane
            items={[
              {
                key: 'original',
                label: 'Хуучин мэдээлэл',
                children: <ProductPanel data={data?.originalData} />,
              },
              {
                key: 'updated',
                label: 'Шинэ мэдээлэл',
                children: (
                  <ProductPanel
                    data={data?.updatedData}
                    fallback={data?.originalData}
                    changedFields={changedFieldSet}
                  />
                ),
              },
            ]}
          />
        </Skeleton>
      </div>
    </div>
  );
};

export default ProductChangesDetail;
